/* eslint-disable react-hooks/exhaustive-deps */
// contexts/NotificationContext.tsx
import { formatDistanceToNow } from "date-fns";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export interface Notification {
  id: string;
  timestamp: string;
  created_at?: string;
  is_read: boolean;
  message: string;
  action?: string;
  object_id?: string;
  status?: string;
  from_user?: string;
  admin_unit?: any;
  timeAgo?: string;
  isNew?: boolean;
  [key: string]: any;
}

export interface NotificationContextType {
  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  unreadCount: number;
  connectionStatus: string;
  error: any;
  markAsRead: (id: string) => void;
  refreshNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [connectionStatus, setConnectionStatus] =
    useState<string>("disconnected");
  const [error, setError] = useState<any>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(false);

  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 5000;

  /** Utility to get user ID from sessionStorage */
  const getUser = (): string | undefined => {
    try {
      const authToken =
        sessionStorage.getItem("authToken") ||
        sessionStorage.getItem("access_token");
      if (!authToken) return undefined;
      const parsedToken = JSON.parse(authToken);
      return parsedToken?.user_id;
    } catch (error) {
      console.error("Error parsing auth token:", error);
      return undefined;
    }
  };

  const user = getUser();
  const baseUrl = process.env.REACT_APP_WS_URL;

  /** Local storage key per user */
  const getLocalStorageKey = (userId: string | undefined) =>
    userId ? `notifications_${userId}` : "notifications";

  /** Load notifications from localStorage */
  useEffect(() => {
    if (user) {
      const key = getLocalStorageKey(user);
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const parsed = JSON.parse(stored) as Notification[];
          setNotifications(parsed);
          setUnreadCount(parsed.filter((n) => !n.is_read).length);
        }
      } catch (error) {
        console.error("Failed to load notifications from localStorage:", error);
      }
    }
  }, [user]);

  /** Save notifications to localStorage */
  const saveToLocalStorage = (notifs: Notification[]) => {
    if (!user) return;
    const key = getLocalStorageKey(user);
    try {
      localStorage.setItem(key, JSON.stringify(notifs));
    } catch (error) {
      console.error("Failed to save notifications to localStorage:", error);
    }
  };

  /** WebSocket URL */
  const getWebSocketUrl = useCallback((): string | null => {
    if (!baseUrl || !user) return null;
    return `${baseUrl}?user_id=${user}`;
  }, [user, baseUrl]);

  /** Update unread count */
  const updateUnreadCount = useCallback((notifs: Notification[]) => {
    setUnreadCount(notifs.filter((n) => !n.is_read).length);
  }, []);

  /** Process incoming notifications */
  const handleNewNotifications = useCallback(
    (data: any) => {
      if (!isMountedRef.current) return;

      let notificationsArray: any[] = [];
      if (Array.isArray(data)) notificationsArray = data;
      else if (data?.data && Array.isArray(data.data))
        notificationsArray = data.data;
      else if (data?.id) notificationsArray = [data];
      else return;

      const formattedNotifications: Notification[] = notificationsArray
        .filter((n) => n && n.id)
        .map((notification) => {
          const timestamp =
            notification.timestamp ||
            notification.created_at ||
            new Date().toISOString();
          return {
            id: notification.id,
            message: notification.message || "New notification",
            is_read: Boolean(notification.is_read),
            timestamp,
            timeAgo: formatDistanceToNow(new Date(timestamp)),
            isNew: !notification.is_read,
            action: notification.action,
            object_id: notification.object_id,
            status: notification.status,
            from_user: notification.from_user,
            admin_unit: notification.admin_unit,
            ...notification,
          };
        });

      if (formattedNotifications.length === 0) return;

      setNotifications((prev) => {
        const newNotifications = formattedNotifications.filter(
          (n) => !prev.some((p) => p.id === n.id)
        );
        const merged = [...newNotifications, ...prev].slice(0, 50);

        saveToLocalStorage(merged);
        updateUnreadCount(merged);

        return merged;
      });
    },
    [updateUnreadCount, user]
  );

  /** Clean up WebSocket */
  const cleanupWebSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onmessage = null;
      socketRef.current.onerror = null;
      socketRef.current.onclose = null;
      if (socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close(1000, "Cleanup");
      }
      socketRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
  }, []);

  /** Connect to WebSocket */
  const connectWebSocket = useCallback(() => {
    if (
      !isMountedRef.current ||
      reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS
    )
      return;

    cleanupWebSocket();

    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      setConnectionStatus("error");
      return;
    }

    setConnectionStatus("connecting");

    try {
      socketRef.current = new WebSocket(wsUrl);

      socketRef.current.onopen = () => {
        if (!isMountedRef.current) return;
        reconnectAttemptsRef.current = 0;
        setConnectionStatus("connected");
        setError(null);
        socketRef.current?.send(
          JSON.stringify({ type: "request_notifications" })
        );
      };

      socketRef.current.onmessage = (event) => {
        if (!isMountedRef.current) return;
        try {
          const message = JSON.parse(event.data);
          handleNewNotifications(message.data || message);
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
        }
      };

      socketRef.current.onerror = (error) => {
        if (!isMountedRef.current) return;
        setConnectionStatus("error");
        setError("WebSocket connection error");
      };

      socketRef.current.onclose = (event) => {
        if (!isMountedRef.current) return;
        if (!event.wasClean) {
          setConnectionStatus("disconnected");
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(
            RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1),
            30000
          );
          setTimeout(connectWebSocket, delay);
        } else setConnectionStatus("disconnected");
      };
    } catch (error) {
      setConnectionStatus("error");
      setError("Failed to create WebSocket connection");
    }
  }, [getWebSocketUrl, handleNewNotifications, cleanupWebSocket]);

  /** Mark notification as read */
  const markAsRead = useCallback(
    (id: string) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(
          JSON.stringify({ type: "mark_as_read", notification_id: id })
        );
      }

      setNotifications((prev) => {
        const updated = prev.map((n) =>
          n.id === id ? { ...n, is_read: true, isNew: false } : n
        );
        saveToLocalStorage(updated);
        updateUnreadCount(updated);
        return updated;
      });
    },
    [updateUnreadCount, user]
  );

  /** Manually refresh notifications */
  const refreshNotifications = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "request_notifications" }));
    }
  }, []);

  /** Mount / unmount */
  useEffect(() => {
    isMountedRef.current = true;
    if (user && baseUrl) connectWebSocket();
    return () => {
      isMountedRef.current = false;
      cleanupWebSocket();
    };
  }, [user, baseUrl, connectWebSocket, cleanupWebSocket]);

  const value: NotificationContextType = {
    notifications,
    setNotifications,
    unreadCount,
    connectionStatus,
    error,
    markAsRead,
    refreshNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

/** Custom hook */
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context)
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  return context;
};
